import { ApolloClient, from, split } from '@apollo/client/core'
import { InMemoryCache } from '@apollo/client/cache'
import config from 'config'
import cookieStorage from 'cookie-storage'
import localStorage from 'local-storage'

import {
  createCFRFTokenLink,
  createErrorLink,
  createHttpLink,
  createWSLink,
  createRetryLink,
  createAPIKeyLink,
  type ModifyOptions,
} from './util/links'
import { defaultTypePolicies, legacyTypePolicies } from './util/cache'


const fetch = window.fetch

const modifyOptions: ModifyOptions = (options) => {
  const tenantId = cookieStorage.getItem('scnt_tenant_id')

  const userHeaders = {
    'scnt-current-page-url': window.location.pathname,
  }

  if (tenantId) {
    userHeaders['x-tenant-id'] = tenantId
  }

  return {
    ...options,
    headers: {
      ...options.headers,
      ...userHeaders,
    },
  }
}

const createDefaultClient = () => {
  const httpLink = createHttpLink({
    name: 'GraphQL',
    fetch,
    uri: config.publicApi,
    timeout: config.publicApiTimeout,
    modifyOptions,
  })

  const wsLink = createWSLink({
    uri: config.publicWSApi,
    connectionParams: () => {
      return {
        'X-Auth-Token': localStorage.getSessionItem('tokenApi'),
      }
    },
  })

  // Alternative authentication method
  // const subscriptionMiddleware = {
  //   applyMiddleware: function(options, next) {
  //     options['X-Auth-Token'] = localStorage.getSessionItem('tokenApi')
  //     next()
  //   },
  // }
  //
  // wsLink.subscriptionClient.use([ subscriptionMiddleware ])

  const splitLink = split(
    ({ getContext }) => {
      // small optimization, because we use subscription only for payment session
      const context = getContext()
      return context.webSocket
    },
    wsLink,
    from([
      createRetryLink(),
      createCFRFTokenLink(),
      httpLink,
    ])
  )

  const link = from([
    createErrorLink(),
    splitLink,
  ])

  const cache = new InMemoryCache({
    typePolicies: defaultTypePolicies,
  })

  return new ApolloClient({
    cache: cache.restore(window.__APOLLO_STATE__.default),
    link,
    connectToDevTools: true,
    assumeImmutableResults: true,
  })
}

const createLegacyClient = () => {
  const link = from([
    createErrorLink(),
    createRetryLink(),
    createHttpLink({
      name: 'Legacy',
      fetch,
      uri: config.publicLegacyApi,
      timeout: config.publicApiTimeout,
      modifyOptions,
    }),
  ])

  const cache = new InMemoryCache({
    typePolicies: legacyTypePolicies,
  })

  return new ApolloClient({
    cache: cache.restore(window.__APOLLO_STATE__.legacy),
    link,
    // ATTN replace to true if you need it
    connectToDevTools: false,
    assumeImmutableResults: true,
  })
}

const createStrapiClient = () => {
  const link = from([
    createErrorLink(),
    createRetryLink(),
    createAPIKeyLink(config.publicStrapiApiKey),
    createHttpLink({
      name: 'Strapi',
      fetch,
      uri: config.publicStrapiApi,
      timeout: config.publicApiTimeout,
      modifyOptions,
    }),
  ])

  const cache = new InMemoryCache({
    typePolicies: defaultTypePolicies,
  })

  return new ApolloClient({
    cache: cache.restore(window.__APOLLO_STATE__.strapi),
    link,
    // ATTN replace to true if you need it
    connectToDevTools: false,
    assumeImmutableResults: true,
  })
}

export const createClients = () => {
  return {
    default: createDefaultClient(),
    legacy: createLegacyClient(),
    strapi: createStrapiClient(),
  }
}
